/**
 * Production-Ready - TaxableValueBuilder
 * Mirrors engine's taxable-value-map-builder.ts functionality
 * Backend integration pending (see docs/planning/bond-calculator-app-plan.md)
 */

import { TIME_PERIODS } from '~/config/financial-constants';
import { DateCalculationEngine } from '~/engine/processing/shared/date-calculations';
import type { BondSeries, TaxableValueMapEntry, TaxValueInputs } from '~/types';
import type { TaxableValueOverrides } from '~/types/overrides';
import type { DateRecordsMapByPaymentDate } from '~/types/project';
import {
  calculateRequiredProjectionYears,
  calculateTaxableValueSummary,
  projectTaxableValues,
  validateGrowthRate,
  validateStartingValue,
} from './TaxableValueHelper';

export class TaxableValueBuilder {
  /**
   * Build taxable value map with dynamic projection length based on bond timeline
   * This is the primary entry point that should be used by all consumers
   *
   * When growthRateOverrides are provided, they replace the default rates for specific years,
   * creating a cascading effect on all subsequent year values.
   */
  static build(
    inputs: TaxValueInputs,
    startingTaxYear?: number,
    bondSeries?: BondSeries[],
    dateRecordsMapByPaymentDate?: DateRecordsMapByPaymentDate,
    growthRateOverrides?: TaxableValueOverrides | null
  ): TaxableValueMapEntry[] {
    const baseYear = startingTaxYear || DateCalculationEngine.getCurrentYear();

    // Calculate required projection years dynamically
    const projectionYears = calculateRequiredProjectionYears(
      baseYear,
      bondSeries,
      dateRecordsMapByPaymentDate
    );

    return projectTaxableValues(inputs, baseYear, projectionYears, growthRateOverrides);
  }

  /**
   * Legacy build method for backward compatibility
   * @deprecated Use build() with bond data for proper dynamic projection
   */
  static buildWithFixedYears(
    inputs: TaxValueInputs,
    startingTaxYear?: number,
    years: number = TIME_PERIODS.PROJECT_YEARS,
    growthRateOverrides?: TaxableValueOverrides | null
  ): TaxableValueMapEntry[] {
    const baseYear = startingTaxYear || DateCalculationEngine.getCurrentYear();
    return projectTaxableValues(inputs, baseYear, years, growthRateOverrides);
  }

  /**
   * Validates input parameters before building
   */
  static validateInputs(inputs: TaxValueInputs): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    errors.push(...validateStartingValue(inputs.startingYearTaxableValue));
    errors.push(...validateGrowthRate(inputs.startingYearGrowthRate, 'Starting year growth rate'));
    errors.push(...validateGrowthRate(inputs.fiveYearRate, 'Five year growth rate'));
    errors.push(...validateGrowthRate(inputs.twentyYearRate, 'Twenty year growth rate'));

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Builds a summary of the taxable value map
   */
  static buildSummary(data: TaxableValueMapEntry[]) {
    return calculateTaxableValueSummary(data);
  }
}
